import { ClerkProvider } from '@clerk/clerk-react'
import { esES, arSA, zhCN, frFR, ptBR, ruRU } from '@clerk/localizations'
import { shadcn } from '@clerk/themes'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type LocalizationObject =
  | typeof esES
  | typeof arSA
  | typeof zhCN
  | typeof frFR
  | typeof ptBR
  | typeof ruRU
  | {
      userButton?: {
        action__manageAccount?: string
        action__signOut?: string
      }
      userProfile?: {
        navbar?: {
          title?: string
          description?: string
          account?: string
          security?: string
        }
        start?: {
          profileSection?: {
            title?: string
          }
          headerTitle__account?: string
          headerTitle__security?: string
        }
      }
    }

function withUserProfileOverrides(
  base: LocalizationObject,
  overrides: {
    manageAccount: string
    signOut?: string
    accountTitle: string
    accountDescription: string
    profile: string
    security: string
  }
): LocalizationObject {
  return {
    ...base,
    userButton: {
      ...base.userButton,
      action__manageAccount: overrides.manageAccount,
      ...(overrides.signOut ? { action__signOut: overrides.signOut } : {}),
    },
    userProfile: {
      ...base.userProfile,
      navbar: {
        ...base.userProfile?.navbar,
        title: overrides.accountTitle,
        description: overrides.accountDescription,
        account: overrides.profile,
        security: overrides.security,
      },
      start: {
        ...base.userProfile?.start,
        profileSection: {
          ...base.userProfile?.start?.profileSection,
          title: overrides.profile,
        },
        headerTitle__account: overrides.profile,
        headerTitle__security: overrides.security,
      },
    },
  }
}

export function LocalizedClerkProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const [clerkLocalization, setClerkLocalization] = useState<LocalizationObject>({})

  useEffect(() => {
    // Update Clerk localization when language changes
    if (i18n.language.startsWith('es')) {
      setClerkLocalization(
        withUserProfileOverrides(esES, {
          manageAccount: 'Administrar Mi Cuenta',
          accountTitle: 'Cuenta',
          accountDescription: 'Administra la informacion de tu cuenta.',
          profile: 'Perfil',
          security: 'Seguridad',
        })
      )
    } else if (i18n.language.startsWith('fr')) {
      setClerkLocalization(
        withUserProfileOverrides(frFR, {
          manageAccount: 'Gerer mon compte',
          accountTitle: 'Compte',
          accountDescription: 'Gerez les informations de votre compte.',
          profile: 'Profil',
          security: 'Securite',
        })
      )
    } else if (i18n.language.startsWith('pt')) {
      setClerkLocalization(
        withUserProfileOverrides(ptBR, {
          manageAccount: 'Gerenciar Minha Conta',
          accountTitle: 'Conta',
          accountDescription: 'Gerencie as informacoes da sua conta.',
          profile: 'Perfil',
          security: 'Seguranca',
        })
      )
    } else if (i18n.language.startsWith('ru')) {
      setClerkLocalization(
        withUserProfileOverrides(ruRU, {
          manageAccount: 'Управление аккаунтом',
          accountTitle: 'Аккаунт',
          accountDescription: 'Управляйте данными своего аккаунта.',
          profile: 'Профиль',
          security: 'Безопасность',
        })
      )
    } else if (i18n.language.startsWith('fa')) {
      // Farsi - create custom localization since Clerk may not have official support
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Modiriyat hesab man',
            signOut: 'Khoroj',
            accountTitle: 'Hesab',
            accountDescription: 'Etelaat hesab khod ra modiriyat konid.',
            profile: 'Profile',
            security: 'Amniyat',
          }
        )
      )
    } else if (i18n.language.startsWith('ar')) {
      setClerkLocalization(
        withUserProfileOverrides(arSA, {
          manageAccount: 'إدارة حسابي',
          accountTitle: 'الحساب',
          accountDescription: 'أدر معلومات حسابك.',
          profile: 'الملف الشخصي',
          security: 'الأمان',
        })
      )
    } else if (i18n.language.startsWith('zh')) {
      setClerkLocalization(
        withUserProfileOverrides(zhCN, {
          manageAccount: '管理我的账户',
          accountTitle: '账户',
          accountDescription: '管理您的账户信息。',
          profile: '个人资料',
          security: '安全',
        })
      )
    } else if (i18n.language.startsWith('uk')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Керувати моїм обліковим записом',
            accountTitle: 'Обліковий запис',
            accountDescription: 'Керуйте даними свого облікового запису.',
            profile: 'Профіль',
            security: 'Безпека',
          }
        )
      )
    } else if (i18n.language.startsWith('tr')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Hesabimi Yonet',
            accountTitle: 'Hesap',
            accountDescription: 'Hesap bilgilerinizi yonetin.',
            profile: 'Profil',
            security: 'Guvenlik',
          }
        )
      )
    } else if (i18n.language.startsWith('vi')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Quan ly tai khoan cua toi',
            accountTitle: 'Tai khoan',
            accountDescription: 'Quan ly thong tin tai khoan cua ban.',
            profile: 'Ho so',
            security: 'Bao mat',
          }
        )
      )
    } else if (i18n.language.startsWith('tl')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Pamahalaan ang Aking Account',
            accountTitle: 'Account',
            accountDescription: 'Pamahalaan ang impormasyon ng iyong account.',
            profile: 'Profile',
            security: 'Seguridad',
          }
        )
      )
    } else if (i18n.language.startsWith('sw')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Simamia Akaunti Yangu',
            accountTitle: 'Akaunti',
            accountDescription: 'Simamia taarifa za akaunti yako.',
            profile: 'Wasifu',
            security: 'Usalama',
          }
        )
      )
    } else if (i18n.language.startsWith('so')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Maamul Akoonkayga',
            accountTitle: 'Akoon',
            accountDescription: 'Maamul macluumaadka akoonkaaga.',
            profile: 'Profile',
            security: 'Amniga',
          }
        )
      )
    } else if (i18n.language.startsWith('ps')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Zma hesab modiriyat',
            accountTitle: 'Hesab',
            accountDescription: 'Da khpal hesab malumat modiriyat krai.',
            profile: 'Profile',
            security: 'Amniyat',
          }
        )
      )
    } else if (i18n.language.startsWith('pa')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Apna Account Sambhalo',
            accountTitle: 'Account',
            accountDescription: 'Apne account di jankari sambhalo.',
            profile: 'ਪ੍ਰੋਫਾਇਲ',
            security: 'ਸੁਰੱਖਿਆ',
          }
        )
      )
    } else if (i18n.language.startsWith('my')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'ကျွန်ုပ်၏ အကောင့်ကို စီမံရန်',
            accountTitle: 'အကောင့်',
            accountDescription: 'သင့်အကောင့် အချက်အလက်ကို စီမံပါ။',
            profile: 'ပရိုဖိုင်',
            security: 'လုံခြုံရေး',
          }
        )
      )
    } else if (i18n.language.startsWith('lo')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'ຈັດການບັນຊີຂອງຂ້ອຍ',
            accountTitle: 'ບັນຊີ',
            accountDescription: 'ຈັດການຂໍ້ມູນບັນຊີຂອງທ່ານ.',
            profile: 'ໂປຣໄຟລ໌',
            security: 'ຄວາມປອດໄພ',
          }
        )
      )
    } else if (i18n.language.startsWith('ht')) {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Jere Kont Mwen',
            accountTitle: 'Kont',
            accountDescription: 'Jere enfomasyon kont ou.',
            profile: 'Pwofil',
            security: 'Sekirite',
          }
        )
      )
    } else {
      setClerkLocalization(
        withUserProfileOverrides(
          {
            userButton: {},
            userProfile: {},
          },
          {
            manageAccount: 'Manage My Account',
            accountTitle: 'Account',
            accountDescription: 'Manage your account info.',
            profile: 'Profile',
            security: 'Security',
          }
        )
      )
    }
  }, [i18n.language])

  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      proxyUrl={import.meta.env.VITE_CLERK_PROXY_URL}
      localization={clerkLocalization}
      appearance={{ baseTheme: shadcn }}
    >
      {children}
    </ClerkProvider>
  )
}
